import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus } from 'lucide-react'

import { createCollaborationTask } from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoNumberInput, ProtoTextarea } from '@/features/studio/components/ui-kit'

export function BountyTaskCreateModal({
  open,
  onClose,
  productionId,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [reward, setReward] = useState(200)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle('')
    setDescription('')
    setReward(200)
    setError(null)
  }, [open])

  async function handleCreate() {
    const trimmedTitle = title.trim()
    if (!trimmedTitle || reward <= 0) {
      setError(t('tasks.bountyCreateInvalid'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createCollaborationTask(productionId, {
        title: trimmedTitle,
        description: description.trim(),
        reward_points: Math.floor(reward),
        visibility: 'tenant',
        auto_join_production: true,
      })
      onCreated()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'tasks.bountyCreateFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('tasks.bountyCreateTitle')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || !title.trim()} onClick={() => void handleCreate()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('tasks.bountyCreateDraft')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-xs text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="space-y-3">
        <div>
          <ProtoLabel>{t('tasks.colTitle')}</ProtoLabel>
          <ProtoInput
            placeholder={t('tasks.bountyTitlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>
        <div>
          <ProtoLabel>{t('tasks.colDescription')}</ProtoLabel>
          <ProtoTextarea
            rows={3}
            placeholder={t('tasks.bountyDescPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <ProtoLabel>{t('tasks.colReward')}</ProtoLabel>
          <ProtoNumberInput
            min={1}
            value={reward}
            onValueChange={setReward}
            disabled={submitting}
            placeholder={t('tasks.bountyReward')}
          />
        </div>
      </div>
    </ProtoModal>
  )
}
